(function(angular) {
    'use strict';

    angular.module('GorillaBoy.services')
    .constant('SystemTags', {
        SHARED_WITH_ME: 'SHARED_WITH_ME',
        AWAITING_MY_FEEDBACK: 'AWAITING_FEEDBACK',
        OUT_FOR_REVIEW: 'OUT_FOR_REVIEW'
    })
    .factory('FileService', [
        '$rootScope',
        '$http',
        '$log',
        '$q',
        '$interpolate',
        'ServiceHelper',
        'EndpointService',
        'BaseService',
        'AppConstants',
        'SystemTags',
        'MessageManager',
        'MessageProvider',
        FileService
    ]);

    function FileService($rootScope, $http, $log, $q, $interpolate, ServiceHelper, EndpointService, BaseService, AppConstants, SystemTags, MessageManager, MessageProvider) {

        var ALLOWED_PARAMS = ['sortBy', 'order', 'maxItems'],
            userInfoContext = null;

        // AppConstant bindings
        var
            httpMethods = AppConstants.HttpMethods,
            deleteTypes = AppConstants.DeleteTypes;

            FileService.REDUCE_FUNCTIONS = {

                reduceFolderContents : function(dataList){
                    var documents = [], folders = [];

                    dataList.forEach(function(thisPageData){
                        var thisPageDocList = (thisPageData.Folder ? thisPageData.Folder.Documents : thisPageData.Documents);
                        var thisPageFolderList = (thisPageData.Folder ? thisPageData.Folder.Folders : thisPageData.Folders);
                        if (thisPageDocList && thisPageDocList.length) {
                            documents = documents.concat(thisPageDocList);
                        }
                        if (thisPageFolderList && thisPageFolderList.length) {
                            folders = folders.concat(thisPageFolderList);
                        }
                    });

                    var result = {};
                    result.data = angular.copy(dataList[0]);
                    if(result.data.Folder){
                        result.data.Folder.Documents = documents;
                        result.data.Folder.Folders = folders;
                    }else{
                        result.data.Documents = documents;
                        result.data.Folders = folders;
                    }
                    result.data.Marker = "";
                    return result;
                }
            };


        function sendRequest(request, getAllPages, reduceFunction) {
            var deferred = $q.defer();

            var successCallback = function(result) {
                var data;
                if (result.status !== 204) {
                    data = result.data;
                }
                deferred.resolve(data);
            };

            var errorCallback =  function(result) {
                var errorMessage = ServiceHelper.formatFailure(result.data, result.status, result.statusText);
                deferred.reject({ message: errorMessage, result: result });
            };

            if(getAllPages){
                BaseService.getAllPages(request, reduceFunction)
                           .then(successCallback, errorCallback);
            }else{
                $http(request).then(successCallback, errorCallback);
            }

            return deferred.promise;
        }

        // Returns an object that has all the key/value of `params` that are marked as allowed
        // in the `allowedParams` array
        function scrubParams(params, allowedParams) {
            var cleanParams = {},
                i, allowed;

            if (!params) {
                return cleanParams;
            }

            for (i = 0; i < allowedParams.length; i += 1) {
                allowed = allowedParams[i];
                if (params.hasOwnProperty(allowed)) {
                    cleanParams[allowed] = params[allowed];
                }
            }
            return cleanParams;
        }

        function setUserContext(userInfo) {
            var
                requiredProperties = ['Id', 'RootFolderId', 'RecycleBinFolderId'],
                i;

            // Ensure we have the data we need from this object.
            for (i = 0; i < requiredProperties.length; i += 1) {
                if (!userInfo.hasOwnProperty(requiredProperties[i])) {
                    throw { error: 'Missing property "' + requiredProperties[i] + '" on user object' };
                }
            }

            userInfoContext = userInfo;
        }

        function ensureUserContext() {
            if (!userInfoContext) {
                throw { error: 'No userContext set on FileService' };
            }
        }

        /*
         * @ngdoc method
         * @name FileService#createFolder
         *
         * @description
         * Creates a new folder with the specified `folderName` as a child of folder `parentFolderId`
         *
         * @param {string} folderName The name of the folder to create
         * @param {string} parentFolderId The ID of the folder that will contain the new folder.
         * @returns {Promise} Future object with the following set of properties:
         *
         * - **Folder** – `{object}` – An object with information about the folder:
         *   - **Documents** - `{array}` - an array with the files fetched in the current page, will be `null`
         *   - **Folders** - `{array}` - an array with the folders fetched in the current page, will be `null`
         *   - **Metadata** - `{object}` - metadata about the folder
         *   - **SharedRootFolderMetadata** - [null]
         * - **Marker** – {string} – A marker ID used for fetching multiple pages of data. `null` when all data has been fetched.
         *
         */
        function createFolder(folderName, parentFolderId) {
            var req;

            $log.debug('[FileService] Creating folder "' + folderName + '"" (parent_id: ' + parentFolderId + ')');

            req = {
                method: httpMethods.POST,
                url: ServiceHelper.interpolateUrl(':apiRoot/folder'),
                data: {
                    FolderName: folderName,
                    ParentFolderId: parentFolderId,
                    exclude: ['OLD_PERMISSIONS']
                }
            };
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("FileService.createFolder");
            var promise = sendRequest(req);
            promise.then(null, function(response) {
                $rootScope.metricServiceInstance.putMetric({
                    MethodName: "FileService.Error." + response.result.status + ".createFolder",
                    MetricName: "Count",
                    Unit: "Count",
                    Values: 1
                });
            });
            promise.finally(function() {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        }

        /*
         * @ngdoc method
         * @name FileService#getFolder
         *
         * @description
         * Gets the specified folder metadata and content
         *
         * @param {string} folderId The ID of the folder to retrieve
         * @returns {Promise} Future object with the following set of properties:
         *
         * - **Folder** – `{object}` – An object with information about the folder:
         *   - **Documents** - `{array}` - an array with the files fetched in the current page
         *   - **Folders** - `{array}` - an array with the folders fetched in the current page
         *   - **Metadata** - `{object}` - metadata about the folder
         *   - **SharedRootFolderMetadata** - [null]
         * - **Marker** – {string} – A marker ID used for fetching multiple pages of data. `null` when all data has been fetched.
         *
         */
        function getFolder(folderId, params, getAllPages) {
            var req;

            if (params && params.marker) {
                $log.debug('[FileService] Fetching folder (id: ' + folderId + ') with marker');
            } else {
                $log.debug('[FileService] Fetching folder (id: ' + folderId + ')');
            }
            params.includeDrafts = true;
            req = {
                method: httpMethods.GET,
                url: ServiceHelper.interpolateUrl(':apiRoot/folder/:folderId', { folderId: folderId }),
                params: params
            };

            if (req.params) {
                req.params.exclude = ['OLD_PERMISSIONS'];
            }
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("FileService.getFolder");
            var promise = sendRequest(req, getAllPages, FileService.REDUCE_FUNCTIONS.reduceFolderContents);
            promise.then(null, function(response) {
                $rootScope.metricServiceInstance.putMetric({
                    MethodName: "FileService.Error." + response.result.status + ".getFolder",
                    MetricName: "Count",
                    Unit: "Count",
                    Values: 1
                });
            });
            promise.finally(function() {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        }

        /**
         * This method calls the new API and gets the folder metadata without childrens' details
         */
        function getFolderNewApi(folderId) {
            var req = {
                method: httpMethods.GET,
                url: EndpointService.endpoint(true) + 'folders/' + encodeURIComponent(folderId),
                params : {
                    exclude : 'OLD_PERMISSIONS'
                }
            };
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("FileService.getFolderNewApi");
            var promise = $http(EndpointService.tokenize(req));
            promise.then(null, function(response) {
                $rootScope.metricServiceInstance.putMetric({
                    MethodName: "FileService.Error." + response.status + ".getFolderNewApi",
                    MetricName: "Count",
                    Unit: "Count",
                    Values: 1
                });
            });
            promise.finally(function() {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        }


        /**
         * This method calls the new API and gets the folder metadata without childrens' details
         */
        function batchGetFolder(folderIds) {
            var req = {
                method: httpMethods.GET,
                url: EndpointService.endpoint(true) + 'batch/folders',
                params : {
                    folderIds : folderIds.join(',')
                }
            };
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("FileService.getFolderNewApi");
            var promise = $http(EndpointService.tokenize(req));
            promise.then(null, function(response) {
                $rootScope.metricServiceInstance.newCounterMetric("FileService.Error.batchGetFolder");
            });
            promise.finally(function() {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        }

        /**
         * This method calls the new API and gets the folder metadata without childrens' details
         */
        function describeFolderContents(folderId) {
            var req = {
                method: httpMethods.GET,
                url: EndpointService.endpoint(true) + 'folders/' + encodeURIComponent(folderId) + '/contents'
            };
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("FileService.describeFolderContents");
            var promise = $http(EndpointService.tokenize(req));
            promise.then(null, function(response) {
                $rootScope.metricServiceInstance.putMetric({
                    MethodName: "FileService.Error." + response.status + ".describeFolderContents",
                    MetricName: "Count",
                    Unit: "Count",
                    Values: 1
                });
            });
            promise.finally(function() {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        }

        /*
         * @ngdoc method
         * @name FileService#getFolderContents
         *
         * @description
         * Gets the specified folder contents
         *
         * @param {string} folderId The ID of the folder to retrieve
         * @param {object} params The parameters of this API call:
         *  - **sort** - {string} - Sort By options (DATE, NAME, etc.)
         *  - **order** - {string} - ASCENDING or DESCENDING
         *  - **limit** - {int} - The number of items returned will be Limit - 1
         *  - **type** - {string} - The type of resources we are interested in: DOCUMENT, FOLDER, or ALL
         *  - **marker** - {string} - A marker ID used for fetching multiple pages of data.
         * @returns {Promise} Future object with the following set of properties:
         *
         * - **Documents** - `{array}` - an array with the files fetched in the current page
         * - **Folders** - `{array}` - an array with the folders fetched in the current page
         * - **Marker** – {string} – A marker ID used for fetching multiple pages of data. `null` when all data has been fetched.
         *
         */
        function getFolderContents(folderId, params) {
            var req;

            if (params && params.marker) {
                $log.debug('[FileService] Fetching contents of folder (id: ' + folderId + ') with marker');
            } else {
                $log.debug('[FileService] Fetching contents of folder (id: ' + folderId + ')');
            }

            req = {
                method: httpMethods.GET,
                url: ServiceHelper.interpolateUrl(':apiRoot/folder/:folderId/contents', { folderId: folderId }),
                params: params
            };
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("FileService.getFolderContents");
            var promise = sendRequest(req);
            promise.then(null, function(response) {
                $rootScope.metricServiceInstance.putMetric({
                    MethodName: "FileService.Error." + response.result.status + ".getFolderContents",
                    MetricName: "Count",
                    Unit: "Count",
                    Values: 1
                });
            });
            promise.finally(function() {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        }

        /*
         * @ngdoc method
         * @name FileService#getByTag
         *
         * @description
         * Gets files and folders that match the specified `tagName`.
         * Currently, only "SHARED_WITH_ME", "AWAITING_FEEDBACK", and "OUT_FOR_REVIEW" are supported.
         *
         * @param {string} tagName The name of the tag for which to find content.
         * @returns {Promise} Future object with the following set of properties:
         *
         * - **Documents** - `{array}` - an array with the files fetched in the current page
         * - **Folders** - `{array}` - an array with the folders fetched in the current page
         *
         */
        function getByTag(tagName, params) {
            var req, cleanParams;

            // $log.debug('[FileService] Fetching files by tag name (tag: ' + tagName + ')');

            // Should we be rejecting a promise that we return instead?
            if (!isTagValid(tagName)) {
                throw { error: 'Invalid tag "' + tagName + '"' };
            }

            if (tagName === SystemTags.SHARED_WITH_ME) {
                req = {
                    method: httpMethods.GET,
                    url: ServiceHelper.interpolateUrl(':apiRoot/collection/resource'),
                    params: {
                        tag: SystemTags.SHARED_WITH_ME
                    }
                };
            } else {
                cleanParams = scrubParams(params, ALLOWED_PARAMS);
                cleanParams.type = tagName;

                req = {
                    method: httpMethods.GET,
                    url: ServiceHelper.interpolateUrl(':apiRoot/document'),
                    params: cleanParams
                };
            }
            req.params.includeDrafts = true;
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("FileService.getByTag_" + tagName);
            var promise = sendRequest(req);
            promise.then(null, function(response) {
                $rootScope.metricServiceInstance.putMetric({
                    MethodName: "FileService.Error." + response.result.status + ".getByTag_" + tagName,
                    MetricName: "Count",
                    Unit: "Count",
                    Values: 1
                });
            });
            promise.finally(function() {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        }
        
        
        function getSharedByMeResources(startTime, endTime, resourceCategory, marker, params) {
            var req, cleanParams,
            	testPrincipalId = 'PrincipalId_Place_Holder',
                EPOCH_BASE = 1000;

        	if(resourceCategory === 'ALL') {
        		resourceCategory = null;
        	}

	    	var startDate = (startTime)? startTime.setHours(0,0,0) / EPOCH_BASE : null,
		    	endDate = (endTime)? endTime.setHours(23,59,59) / EPOCH_BASE : null;

                req = {
                    method: httpMethods.GET,
                    url: ServiceHelper.interpolateUrl(':apiRoot/resource/:principalId/shared', { principalId: testPrincipalId }),
                    params: {
                    	sharedCategory: resourceCategory,
                    	startTime: startDate,
                    	endTime: endDate,
                    	marker: marker
                    }
                };

            req.params.includeDrafts = true;
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("FileService.getSharedOutResources_" + resourceCategory);
            var promise = sendRequest(req);
            promise.then(null, function(response) {
                $rootScope.metricServiceInstance.putMetric({
                    MethodName: "FileService.Error." + response.status + ".getSharedOutResources_" + resourceCategory,
                    MetricName: "Count",
                    Unit: "Count",
                    Values: 1
                });
            });
            promise.finally(function() {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        }


        function __getFolder(folderType, params) {
            var req, cleanParams;

            cleanParams = scrubParams(params, ALLOWED_PARAMS);
            cleanParams.type = folderType;

            req = {
                method: httpMethods.GET,
                url: ServiceHelper.interpolateUrl(':apiRoot/folder'),
                params: cleanParams
            };

            return sendRequest(req);
        }

        function __getUserFolder(userInfo, paramName, params, getAllPages) {
            var deferred, cleanParams;

            deferred = $q.defer();

            if (!userInfo || !userInfo[paramName]) {
                deferred.reject();
                return deferred.promise;
            }

            cleanParams = scrubParams(params, ALLOWED_PARAMS);
            return getFolder(userInfo[paramName], cleanParams, getAllPages);
        }

        function getSharedWithMeRoot(params) {
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("FileService.getSharedWithMeRoot");
            var promise = __getFolder('SHARED', params);
            promise.then(null, function(response) {
                $rootScope.metricServiceInstance.putMetric({
                    MethodName: "FileService.Error." + response.result.status + ".getSharedWithMeRoot",
                    MetricName: "Count",
                    Unit: "Count",
                    Values: 1
                });
            });
            promise.finally(function() {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        }

        function getRecycleBinRoot(params, getAllPages) {
            ensureUserContext();
            // $log.debug('[FileService] Fetching reycle bin root content');
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("FileService.getRecycleBinRoot");
            var promise = __getUserFolder(userInfoContext, 'RecycleBinFolderId', params, getAllPages);
            promise.then(null, function(response) {
                $rootScope.metricServiceInstance.putMetric({
                    MethodName: "FileService.Error." + response.result.status + ".getRecycleBinRoot",
                    MetricName: "Count",
                    Unit: "Count",
                    Values: 1
                });
            });
            promise.finally(function() {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        }


        /*
         * @ngdoc method
         * @name FileService#updateFileName
         *
         * @description
         * Updates the the name of the specified file.
         *
         * @param {string} fileId The ID of the fuke to be renamed.
         * @param {string} newFileName The new file name.
         * @returns {Promise} Future object
         *
         */
        function updateFileName(fileId, newFileName) {

            var req = {
                method: httpMethods.PATCH,
                url: ServiceHelper.interpolateUrl(':apiRoot/documents/:documentId', { documentId: fileId }, true),
                data: {
                    Name: newFileName
                }
            };
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("FileService.updateFileName");
            var promise = sendRequest(req);
            promise.then(null, function(response) {
                var result = response.result;
                $rootScope.metricServiceInstance.putMetric({
                    MethodName: "FileService.Error." + result.status + ".updateFileName",
                    MetricName: "Count",
                    Unit: "Count",
                    Values: 1
                });
            });
            promise.finally(function() {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        }

        /*
         * @ngdoc method
         * @name FileService#updateFolderName
         *
         * @description
         * Updates the the name of the specified folder.
         *
         * @param {string} folderId The ID of the folder to be renamed.
         * @param {string} newFolderName The new folder name.
         * @returns {Promise} Future object
         *
         */
        function updateFolderName(folderId, newFolderName) {
            var req = {
                method: httpMethods.PATCH,
                url: ServiceHelper.interpolateUrl(':apiRoot/folders/:folderId', { folderId: folderId }, true),
                data: {
                    Name: newFolderName
                }
            };
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("FileService.updateFolderName");
            var promise = sendRequest(req);
            promise.then(null, function(response) {
                var result = response.result;
                $rootScope.metricServiceInstance.putMetric({
                    MethodName: "FileService.Error." + result.status + ".updateFolderName",
                    MetricName: "Count",
                    Unit: "Count",
                    Values: 1
                });                 
            });
            promise.finally(function() {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        }

        function isFile(item) {
            return item.hasOwnProperty('VersionMetadata');
        }

        /*
         * @ngdoc method
         * @name FileService#updateName
         *
         * @description
         * Updates the the name of the specified file or folder.
         *
         * @param {object} fileOrFolder The instance of the file or folder to be renamed.
         * @param {string} newName The new file/folder name.
         * @returns {Promise} Future object
         *
         */
        function updateName(fileOrFolder, newName) {            
            var NAME_INVALID = 'Names cannot include';
            var deferred = $q.defer();
            var oldDocOrFolderName = isFile(fileOrFolder) ? fileOrFolder.VersionMetadata.Name : fileOrFolder.Name;
            
            if (newName === oldDocOrFolderName) {
                MessageManager.clearTransientMessages();
                deferred.reject();
                return deferred.promise;
            }
            
            if (newName.length > AppConstants.LIMITS.RESOURCE_NAME_CHAR_LIMIT) {
                // Error case: file name too long
                MessageManager.addTransientErrorMessage(MessageProvider.renameFailNameTooLong);
                deferred.reject();
                return deferred.promise;
            }

            var oldNameSegments = oldDocOrFolderName.split('.');
            var newNameSegments = newName.split('.');

            if (isFile(fileOrFolder) &&
                    (oldNameSegments.length > 1 && (newNameSegments.length <= 1 || newNameSegments.pop() !== oldNameSegments.pop()))) {
                // Error case: remove/modify file extension
                MessageManager.addTransientErrorMessage(MessageProvider.renameFailIllegalExtensionChange);
                deferred.reject();
                return deferred.promise;
            }

            updateNameImpl(fileOrFolder, newName)
            .then(function() {
                MessageManager.clearTransientMessages();
                deferred.resolve();
                if (isFile(fileOrFolder)) {
                    MessageManager.addSuccessMessage(MessageProvider.renameFileSuccess);
                } else {
                    MessageManager.addSuccessMessage(MessageProvider.renameFolderSuccess);
                }
            }, function(reason) {
                if (reason.message && reason.message.indexOf('EntityAlreadyExistsException') >= 0) {
                    // Error case: naming conflict
                    MessageManager.addTransientErrorMessage(MessageProvider.renameFailNamingConflict);
                } else if (reason.message && reason.message.indexOf(NAME_INVALID) > 0) {
                    MessageManager.addTransientErrorMessage(MessageProvider.renameInvalidName);
                } else {
                    // All other error cases
                    MessageManager.addTransientErrorMessage(MessageProvider.renameFailGeneralError);
                }
                deferred.reject();
            });
            return deferred.promise;
        }

        function updateNameImpl(fileOrFolder, newName) {
            if (isFile(fileOrFolder)) {
                return updateFileName(fileOrFolder.Id, newName);
            }
            return updateFolderName(fileOrFolder.Id, newName);
        }


        function isTagValid(tagName) {
            // For now, we only support AWAITING_FEEDBACK and OUT_FOR_REVIEW
            return tagName === SystemTags.SHARED_WITH_ME || tagName === SystemTags.AWAITING_MY_FEEDBACK || tagName === SystemTags.OUT_FOR_REVIEW;
        }

        function deleteFileImpl(fileId, deletionType) {
            var req, deferred;

            $log.debug('[FileService] Deleting file (id:' + fileId + ', mode: ' + deletionType + ' )');
            if (deletionType === deleteTypes.DELETE_TYPE_RECYCLE) {
                req = {
                    method: httpMethods.PATCH,
                    url: ServiceHelper.interpolateUrl(':apiRoot/documents/:documentId', { documentId: fileId }, true),
                    data: {
                        ResourceState: 'RECYCLED'
                    }
                };
            } else {
                req = {
                    method: httpMethods.DELETE,
                    url: ServiceHelper.interpolateUrl(':apiRoot/documents/:documentId', { documentId: fileId }, true)
                };
            }

            deferred = $q.defer();

            // Augment the reject reason with fileType and fileId
            sendRequest(req).then(function(result) {
                deferred.resolve(result);
            }, function(reason) {
                reason = reason || {};
                reason.fileType = 'file';
                reason.fileId = fileId;

                deferred.reject(reason);
            });
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("FileService.deleteFileImpl");
            var promise = deferred.promise;
            promise.then(null, function(response) {
                $rootScope.metricServiceInstance.putMetric({
                    MethodName: "FileService.Error." + response.result.status + ".deleteFileImpl",
                    MetricName: "Count",
                    Unit: "Count",
                    Values: 1
                });
            });
            promise.finally(function() {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        }

        function deleteFolderImpl(folderId, deletionType) {
            var req, deferred;

            $log.debug('[FileService] Deleting folder (id:' + folderId + ', mode: ' + deletionType + ' )');

            if (deletionType === deleteTypes.DELETE_TYPE_RECYCLE) {
                req = {
                    method: httpMethods.PATCH,
                    url: ServiceHelper.interpolateUrl(':apiRoot/folders/:folderId', { folderId: folderId }, true),
                    data: {
                        ResourceState: 'RECYCLED'
                    }
                };
            } else {
                req = {
                    method: httpMethods.DELETE,
                    url: ServiceHelper.interpolateUrl(':apiRoot/folders/:folderId', { folderId: folderId }, true)
                };
            }

            deferred = $q.defer();
            // Augment the reject reason with fileType and fileId
            sendRequest(req).then(function(result) {
                deferred.resolve(result);
            }, function(reason) {
                reason = reason || {};
                reason.fileType = 'folder';
                reason.folderId = folderId;

                deferred.reject(reason);
            });
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("FileService.deleteFolderImpl");
            var promise = deferred.promise;
            promise.then(null, function(response) {
                $rootScope.metricServiceInstance.putMetric({
                    MethodName: "FileService.Error." + response.result.status + ".deleteFolderImpl",
                    MetricName: "Count",
                    Unit: "Count",
                    Values: 1
                });
            });
            promise.finally(function() {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        }

        /*
         * @ngdoc method
         * @name FileService#moveFileToTrash
         *
         * @description
         * Moves the specified file to the Trash (aka recycle bin)
         *
         * @param {string} fileId The ID of the file to move to the Trash.
         * @returns {Promise} Future object
         *
         */
        function moveFileToTrash(fileId) {
            return deleteFileImpl(fileId, deleteTypes.DELETE_TYPE_RECYCLE);
        }

        /*
         * @ngdoc method
         * @name FileService#deleteFilePermanently
         *
         * @description
         * Permanently deletes the specified file (cannot be undone).
         *
         * @param {string} fileId The ID of the file to delete.
         * @returns {Promise} Future object
         *
         */
        function deleteFilePermanently(fileId) {
            return deleteFileImpl(fileId, deleteTypes.DELETE_TYPE_PERMANENT);
        }

        function restoreFileFromTrash(fileId) {
            var req, deferred;

            $log.debug('[FileService] Restoring file (id:' + fileId + ')');

            req = {
                method: httpMethods.PATCH,
                url: ServiceHelper.interpolateUrl(':apiRoot/documents/:documentId', { documentId: fileId }, true),
                data: {
                    ResourceState: 'ACTIVE'
                }
            };

            deferred = $q.defer();

            // Augment the reject reason with fileType and fileId
            sendRequest(req).then(function(result) {
                deferred.resolve(result);
            }, function(reason) {
                reason = reason || {};
                reason.fileType = 'file';
                reason.fileId = fileId;

                deferred.reject(reason);
            });
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("FileService.restoreFileFromTrash");
            var promise = deferred.promise;
            promise.then(null, function(response) {
                $rootScope.metricServiceInstance.putMetric({
                    MethodName: "FileService.Error." + response.result.status + ".restoreFileFromTrash",
                    MetricName: "Count",
                    Unit: "Count",
                    Values: 1
                });
            });
            promise.finally(function() {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        }

        /*
         * @ngdoc method
         * @name FileService#moveFolderToTrash
         *
         * @description
         * Moves the specified folder to the Trash (aka recycle bin)
         *
         * @param {string} folderId The ID of the folder to move to the Trash.
         * @returns {Promise} Future object
         *
         */
        function moveFolderToTrash(folderId) {
            return deleteFolderImpl(folderId, deleteTypes.DELETE_TYPE_RECYCLE);
        }

        /*
         * @ngdoc method
         * @name FileService#deleteFolderPermanently
         *
         * @description
         * Permanently deletes the specified folder (cannot be undone).
         *
         * @param {string} folderId The ID of the folder to delete.
         * @returns {Promise} Future object
         *
         */
        function deleteFolderPermanently(folderId) {
            return deleteFolderImpl(folderId, deleteTypes.DELETE_TYPE_PERMANENT);
        }

        function restoreFolderFromTrash(folderId) {
            var req, deferred;

            $log.debug('[FileService] Restoring folder (id:' + folderId + ')');

            req = {
                method: httpMethods.PATCH,
                url: ServiceHelper.interpolateUrl(':apiRoot/folders/:folderId', { folderId: folderId }, true),
                data: {
                    ResourceState: 'ACTIVE'
                }
            };

            deferred = $q.defer();

            // Augment the reject reason with fileType and fileId
            sendRequest(req).then(function(result) {
                deferred.resolve(result);
            }, function(reason) {
                reason = reason || {};
                reason.fileType = 'folder';
                reason.folderId = folderId;

                deferred.reject(reason);
            });

            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("FileService.restoreFolderFromTrash");
            var promise = deferred.promise;
            promise.then(null, function(response) {
                $rootScope.metricServiceInstance.putMetric({
                    MethodName: "FileService.Error." + response.result.status + ".restoreFolderFromTrash",
                    MetricName: "Count",
                    Unit: "Count",
                    Values: 1
                });
            });
            promise.finally(function() {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        }

        function deleteFolderContent(folderId) {
            var req;

            $log.debug('[FileService] Deleting content of folder (id:' + folderId + ')');

            req = {
                method: httpMethods.DELETE,
                url: ServiceHelper.interpolateUrl(':apiRoot/folders/:folderId/contents', { folderId: folderId }, true)
            };
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("FileService.deleteFolderContent");
            var promise = sendRequest(req);
            promise.then(null, function(response) {
                $rootScope.metricServiceInstance.putMetric({
                    MethodName: "FileService.Error." + response.result.status + ".deleteFolderContent",
                    MetricName: "Count",
                    Unit: "Count",
                    Values: 1
                });
            });
            promise.finally(function() {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        }

        function deleteImpl(params, deletionType) {
            var files, folders, promises = [];

            files = params.files || [];
            folders = params.folders || [];

            $log.debug('[FileService] Deleting ' + files.length + ' files and ' + folders.length + ' folders');

            angular.forEach(files, function(fileId, index) {
                promises.push(deleteFileImpl(fileId, deletionType));
            });

            angular.forEach(folders, function(folderId, index) {
                promises.push(deleteFolderImpl(folderId, deletionType));
            });
            return $q.all(promises);            
        }

        /*
         * @ngdoc method
         * @name FileService#moveToTrash
         *
         * @description
         * Moves the specified collections of `files` and `folders` to the recycle bin.
         *
         * @param {object} params An object with the following properties:
         * - **files** - `{array}` - an array with the IDs of the files to be move to the trash
         * - **folders** - `{array}` - an array with the IDs of the folders to be moved to the trash
         *
         * @returns {Promise} Future object
         *
         */
        function moveToTrash(params) {
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("FileService.moveToTrash");
            var promise = deleteImpl(params, deleteTypes.DELETE_TYPE_RECYCLE);
            promise.then(null, function(response) {
                $rootScope.metricServiceInstance.putMetric({
                    MethodName: "FileService.Error.moveToTrash",
                    MetricName: "Count",
                    Unit: "Count",
                    Values: 1
                });
            });
            promise.finally(function() {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        }

        /*
         * @ngdoc method
         * @name FileService#deletePermanently
         *
         * @description
         * Permanently deletes the specified collections of `files` and `folders` (cannot be undone).
         *
         * @param {object} params An object with the following properties:
         * - **files** - `{array}` - an array with the IDs of the files to be permanently deleted
         * - **folders** - `{array}` - an array with the IDs of the folders to be permanently deleted
         *
         * @returns {Promise} Future object
         *
         */
        function deletePermanently(params) {
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("FileService.deletePermanently");
            var promise = deleteImpl(params, deleteTypes.DELETE_TYPE_PERMANENT);
            promise.then(null, function(response) {
                $rootScope.metricServiceInstance.putMetric({
                    MethodName: "FileService.Error.deletePermanently",
                    MetricName: "Count",
                    Unit: "Count",
                    Values: 1
                });
            });
            promise.finally(function() {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        }

        /*
         * @ngdoc method
         * @name FileService#restoreFromTrash
         *
         * @description
         * Restores the specified collections of `files` and `folders` from the recycle bin.
         *
         * @param {object} params An object with the following properties:
         * - **files** - `{array}` - an array with the IDs of the files to be restored
         * - **folders** - `{array}` - an array with the IDs of the folders to be restored
         *
         * @returns {Promise} Future object
         *
         */
        function restoreFromTrash(params) {
            var files, folders, promises = [];

            files = params.files || [];
            folders = params.folders || [];

            $log.debug('[FileService] Restoring ' + files.length + ' files and ' + folders.length + ' folders');

            angular.forEach(files, function(fileId, index) {
                promises.push(restoreFileFromTrash(fileId));
            });

            angular.forEach(folders, function(folderId, index) {
                promises.push(restoreFolderFromTrash(folderId));
            });
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("FileService.restoreFromTrash");
            var promise = $q.all(promises);
            promise.then(null, function(response) {
                $rootScope.metricServiceInstance.putMetric({
                    MethodName: "FileService.Error.restoreFromTrash",
                    MetricName: "Count",
                    Unit: "Count",
                    Values: 1
                });
            });
            promise.finally(function() {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        }

        /*
         * @ngdoc method
         * @name FileService#emptyTrash
         *
         * @description
         * Permanently deletes the content of the user's recycle bin.
         *
         * @returns {Promise} Future object
         *
         */
        function emptyTrash() {
            ensureUserContext();
            $log.debug('[FileService] Emptying recycle bin');
            return deleteFolderContent(userInfoContext.RecycleBinFolderId);
        }

        function moveIntoFolder(moveId, moveLocation, moveNewParentFolderId) {
            ensureUserContext();

            var config = {
                method: httpMethods.PATCH,
                url: EndpointService.endpoint(true) + moveLocation + moveId,
                data: {
                    ParentFolderId: moveNewParentFolderId
                }
            };
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("FileService.moveIntoFolder");
            var promise = $http(EndpointService.tokenize(config));
            promise.then(null, function(response) {
                $rootScope.metricServiceInstance.putMetric({
                    MethodName: "FileService.Error." + response.status + ".moveIntoFolder",
                    MetricName: "Count",
                    Unit: "Count",
                    Values: 1
                });
            });
            promise.finally(function() {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        }

        function getFolderPath(folderId) {
            var config = {
                method: httpMethods.GET,
                url: ServiceHelper.interpolateUrl(':apiRoot/folders/:folderId/path', { folderId: folderId }, true),
                params: {
                    fields: 'name'
                }
            };
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("FileService.getFolderPath");
            var promise = $http(EndpointService.tokenize(config));
            promise.then(null, function(response) {
                $rootScope.metricServiceInstance.putMetric({
                    MethodName: "FileService.Error." + response.status + ".getFolderPath",
                    MetricName: "Count",
                    Unit: "Count",
                    Values: 1
                });
            });
            promise.finally(function() {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        }

        // Return public API for FileService
        return {
            setUserContext: setUserContext,
            createFolder: createFolder,
            describeFolderContents: describeFolderContents,
            getFolder: getFolder,
            getFolderNewApi : getFolderNewApi,
            batchGetFolder: batchGetFolder,
            getFolderContents: getFolderContents,
            getSharedWithMeRoot: getSharedWithMeRoot,
            getRecycleBinRoot: getRecycleBinRoot,
            getByTag: getByTag,
            getSharedByMeResources: getSharedByMeResources,
            updateName: updateName,
            moveFileToTrash: moveFileToTrash,
            deleteFilePermanently: deleteFilePermanently,
            moveFolderToTrash: moveFolderToTrash,
            deleteFolderPermanently: deleteFolderPermanently,
            moveToTrash: moveToTrash,
            deletePermanently: deletePermanently,
            restoreFromTrash: restoreFromTrash,
            emptyTrash: emptyTrash,
            moveIntoFolder: moveIntoFolder,
            getFolderPath: getFolderPath
        };
    }

}(angular));
